import React, { useState, useRef, useEffect } from "react";
import { Card, Typography, Divider, Button, Modal, Spin } from "antd";
import { PlayCircle, PauseCircle, Image as ImageIcon, FileText, Volume2, Maximize2, RotateCw, ZoomIn, ZoomOut } from "lucide-react";

const { Title, Text, Paragraph } = Typography;

const VisitingNotePreview = ({ visitingRecord }) => {
  // Audio states
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioElement, setAudioElement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  
  // Image states
  const [imagePreviewVisible, setImagePreviewVisible] = useState(false);
  const [currentImage, setCurrentImage] = useState("");
  const [zoomLevel, setZoomLevel] = useState(1);
  const [rotation, setRotation] = useState(0);
  
  // References
  const progressRef = useRef(null);
  const audioRef = useRef(null);
  const progressIntervalRef = useRef(null);

  // Format time in mm:ss
  const formatTime = (seconds) => {
    if (isNaN(seconds)) return "00:00";
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Function to handle audio playback
  const toggleAudio = () => {
    if (!audioElement && visitingRecord?.audioAttachment) {
      setLoading(true);
      const audio = new Audio(visitingRecord.audioAttachment);
      
      audio.onloadedmetadata = () => {
        setDuration(audio.duration);
      };
      
      audio.oncanplaythrough = () => {
        setLoading(false);
        audio.play();
        setAudioPlaying(true);
      };
      
      audio.onended = () => {
        setAudioPlaying(false);
        setCurrentTime(0);
        clearInterval(progressIntervalRef.current);
      };
      
      audio.onerror = () => {
        setLoading(false);
        console.error("Error loading audio");
      };
      
      setAudioElement(audio);
      audioRef.current = audio;
    } else if (audioElement) {
      if (audioPlaying) {
        audioElement.pause();
        setAudioPlaying(false);
        clearInterval(progressIntervalRef.current);
      } else {
        audioElement.play();
        setAudioPlaying(true);
        startProgressTracking();
      }
    }
  };
  
  // Start tracking audio progress
  const startProgressTracking = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    
    progressIntervalRef.current = setInterval(() => {
      if (audioRef.current) {
        setCurrentTime(audioRef.current.currentTime);
      }
    }, 100);
  };
  
  // Handle progress bar click
  const handleProgressClick = (e) => {
    if (!audioElement || !progressRef.current) return;
    
    const rect = progressRef.current.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    const newTime = pos * duration;
    
    audioElement.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // Function to open image preview
  const openImagePreview = (imageUrl) => {
    setCurrentImage(imageUrl);
    setImagePreviewVisible(true);
    // Reset zoom and rotation when opening a new image
    setZoomLevel(1);
    setRotation(0);
  };
  
  // Image controls
  const zoomIn = () => setZoomLevel(prev => Math.min(prev + 0.25, 3));
  const zoomOut = () => setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
  const rotate = () => setRotation(prev => (prev + 90) % 360);

  // Clean up when component unmounts
  useEffect(() => {
    return () => {
      if (audioElement) {
        audioElement.pause();
        setAudioElement(null);
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);
  
  // Start progress tracking when audio is playing
  useEffect(() => {
    if (audioPlaying) {
      startProgressTracking();
    }
  }, [audioPlaying]);

  // If no visiting record is provided, show a placeholder
  if (!visitingRecord) {
    return (
      <Card className="w-full shadow-lg rounded-lg border-0">
        <div className="text-center p-4">
          <Text type="secondary">No visiting note available</Text>
        </div>
      </Card>
    );
  }

  return (
    <Card className="w-full shadow-lg rounded-lg border-0 overflow-hidden">
      <div className="flex flex-col gap-4">
        {/* Header with date and time */}
        <div className="flex justify-between items-center">
          <Title level={4} className="m-0 text-blue-600">Visiting Note</Title>
          <div className="text-right">
            <Text type="secondary">{visitingRecord.date}</Text>
            <Text type="secondary" className="ml-2">{visitingRecord.time}</Text>
          </div>
        </div>
        
        <Divider className="my-2" />
        
        {/* Note text content */}
        <div className="bg-gray-50 p-4 rounded-lg shadow-sm">
          <div className="flex items-center mb-2">
            <FileText size={16} className="mr-2 text-blue-500" />
            <Text strong>Note Content</Text>
          </div>
          <Paragraph className="mb-0">{visitingRecord.visitNote || "No note content available"}</Paragraph>
        </div>
        
        {/* Audio attachment */}
        {visitingRecord.audioAttachment && (
          <div className="bg-gray-50 p-4 rounded-lg shadow-sm">
            <div className="flex items-center mb-2">
              <Volume2 size={16} className="mr-2 text-blue-500" />
              <Text strong>Audio Recording</Text>
            </div>
            
            <div className="flex flex-col gap-2">
              <Button
                onClick={toggleAudio}
                icon={loading ? <Spin size="small" /> : audioPlaying ? <PauseCircle className="mt-1" /> : <PlayCircle className="mt-1"/>}
                className="flex items-center my-auto w-max"
                type="primary"
                ghost
              >
                {loading ? "Loading..." : audioPlaying ? "Pause" : "Play Recording"}
              </Button>
              
              <div className="mt-2">
                <div 
                  className="h-2 bg-gray-200 rounded-full cursor-pointer relative"
                  ref={progressRef}
                  onClick={handleProgressClick}
                >
                  <div 
                    className="h-full bg-blue-500 rounded-full absolute top-0 left-0"
                    style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
                  ></div>
                </div>
                
                <div className="flex justify-between mt-1">
                  <Text type="secondary" className="text-xs">{formatTime(currentTime)}</Text>
                  <Text type="secondary" className="text-xs">{formatTime(duration)}</Text>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Image attachments */}
        {visitingRecord.imageAttachments && visitingRecord.imageAttachments.length > 0 && (
          <div className="bg-gray-50 p-4 rounded-lg shadow-sm">
            <div className="flex items-center mb-2">
              <ImageIcon size={16} className="mr-2 text-blue-500" />
              <Text strong>Images ({visitingRecord.imageAttachments.length})</Text>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {visitingRecord.imageAttachments.map((img, index) => (
                <div key={index} className="aspect-square overflow-hidden rounded-lg cursor-pointer shadow-sm hover:shadow-md transition-shadow bg-white">
                  <img
                    src={img}
                    alt={`Attachment ${index + 1}`}
                    className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                    onClick={() => openImagePreview(img)}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Enhanced Image preview modal */}
      <Modal
        open={imagePreviewVisible}
        footer={null}
        onCancel={() => setImagePreviewVisible(false)}
        width="90%"
        centered
        bodyStyle={{ padding: 0, height: "80vh", display: "flex", flexDirection: "column" }}
      >
        <div className="flex justify-center items-center bg-white text-black bg-opacity-90 relative h-full">
          {/* Image controls */}
          <div className="absolute top-4 right-4 flex bg-slate-500 p-2 rounded-xl text-black gap-2 z-10">
            <Button 
              icon={<ZoomIn />} 
              onClick={zoomIn} 
              className="flex items-center text-black  justify-center"
              ghost
            />
            <Button 
              icon={<ZoomOut />} 
              onClick={zoomOut} 
              className="flex items-center text-black justify-center"
              ghost
            />
            <Button 
              icon={<RotateCw />} 
              onClick={rotate} 
              className="flex items-center justify-center"
              ghost
            />
          </div>

          {/* Image container */}
          <div className="overflow-auto w-full h-full flex items-center justify-center">
            <img
              src={currentImage}
              alt="Preview"
              className="max-w-full max-h-full transition-all duration-200"
              style={{ 
                transform: `scale(${zoomLevel}) rotate(${rotation}deg)`,
                transformOrigin: 'center center',
                width: 'auto',
                backgroundColor:'white',
                height: 'auto',
                maxWidth: '100%',
                maxHeight: '100%'
              }}
            />
          </div>
        </div>
      </Modal>
    </Card>
  );
};

export default VisitingNotePreview;